<script setup lang="ts">
  import {
    EmptyState,
    type EmptyStateProps,
    EmptyStateSurface,
  } from '@tundr/ds-components';
  import { useLocale } from '@tundr/i18n';
  import { centeredContainerClass } from '@tundr/theme';
  import emptyChart from '@tundr/theme-assets/illustrations/empty-chart.png';
  import LoaderContainer from '../../Loader/LoaderContainer.vue';
  import { CardListItem } from '../CardListItem';
  import type { CardListItemProps } from '../CardListItem/CardListItem.types';
  import {
    cardListWrapperClass,
    cardListWrapperCompactClass,
    listHeaderClass,
    listTitleClass,
  } from './CardList.css.ts';

  defineProps<{
    testId?: string;
    items: CardListItemProps[];
    listTitle?: string;
    loading?: boolean;
    noItemsFound?: Pick<
      EmptyStateProps,
      'svgImage' | 'title' | 'description'
    > & {
      subtitle?: string;
    };
    compactPadding?: boolean;
  }>();

  const { t } = useLocale('common');
</script>

<template>
  <LoaderContainer v-if="loading" />
  <div
    v-else
    :class="[
      cardListWrapperClass,
      compactPadding && cardListWrapperCompactClass,
    ]"
    role="list"
    :data-test-id="testId"
  >
    <div :class="listHeaderClass">
      <div :class="listTitleClass">{{ listTitle }}</div>
      <slot name="extra-header-content" />
    </div>
    <CardListItem
      :role="'listitem'"
      v-for="(item, index) in items"
      :key="item.title"
      v-bind="item"
      :test-id="item.testId || `card-${index}`"
    >
      <template #title> <slot name="title" :item="item"></slot> </template>
      <template #container>
        <slot name="container" :item="item"></slot>
      </template>
      <template #extraContent>
        <slot name="extraContent" :item="item"></slot>
      </template>
    </CardListItem>
    <slot v-if="items.length === 0" name="empty-state">
      <div :class="centeredContainerClass">
        <EmptyState
          :surface="EmptyStateSurface.NONE"
          :svg-image="noItemsFound?.svgImage ?? emptyChart"
          :title="noItemsFound?.title ?? t('words.no_items')"
          :description="noItemsFound?.description ?? noItemsFound?.subtitle"
        />
      </div>
    </slot>
  </div>
</template>
